<template>
	<div>
		<div class="row">
			<div class="col">
				<label for="cookType" class="form-label">Cook Type</label>
				<select
					id="cookType"
					class="form-select modern-select"
					:class="{ 'transparent-form-select': transparent }"
					v-model="cookType"
					@change="handleSelection">
					<option value="home-cooked">Home-cooked</option>
					<option value="restaurant">Restaurant</option>
					<option value="store-bought">Store-bought</option>
				</select>
			</div>
			<div class="col">
				<label for="mealType" class="form-label">Meal Type Tag</label>
				<select
					id="mealType"
					class="form-select modern-select"
					:class="{ 'transparent-form-select': transparent }"
					v-model="mealType">
					<option value="default">Default</option>
					<option value="breakfast">Breakfast</option>
					<option value="lunch">Lunch</option>
					<option value="dinner">Dinner</option>
					<option value="snack">Snack</option>
					<option value="dessert">Dessert</option>
				</select>
			</div>
		</div>

		<div v-if="showRestaurantInput" class="mt-4">
			<restaurant-selector
				v-model="restaurantName"
				:transparent="transparent"
				:initialRestaurantName="props.meal?.restaurant_name"
				@customRestaurant="handleCustomRestaurant" />
		</div>
	</div>
</template>

<script setup>
	import { ref, watch, nextTick } from "vue"
	import RestaurantSelector from "./RestaurantSelector.vue"

	const cookType = ref("home-cooked")
	const mealType = ref("default")
	const showRestaurantInput = ref(false)
	const restaurantName = ref("")
	const props = defineProps({
		meal: Object,
		transparent: {
			type: Boolean,
			default: false,
		},
	})
	const emit = defineEmits([
		"cookType",
		"mealType",
		"restaurantName",
		"customRestaurant",
	])

	// Auto-select meal type based on the current time
	const autoMealType = () => {
		const hour = new Date().getHours()
		if (hour >= 5 && hour < 11) return "breakfast"
		else if (hour >= 11 && hour < 16) return "lunch"
		else if (hour >= 16 && hour < 22) return "dinner"
		else return "snack"
	}

	const handleSelection = () => {
		showRestaurantInput.value = cookType.value === "restaurant"

		if (!showRestaurantInput.value) {
			restaurantName.value = props.meal ? props.meal.restaurant_name : ""
		}
	}

	const handleCustomRestaurant = (data) => {
		emit("customRestaurant", data)
	}

	watch(
		cookType,
		(v) => {
			if (cookType.value !== "restaurant") {
				showRestaurantInput.value = false
			}
			emit("cookType", v)
		},
		{ immediate: true }
	)

	watch(
		restaurantName,
		(v) => {
			emit("restaurantName", v)
		},
		{ immediate: true }
	)

	watch(mealType, (v) => emit("mealType", v), { immediate: true })

	// Initialize with props
	if (props.meal) {
		cookType.value = props.meal.cook_type
		mealType.value =
			props.meal.meal_type === "default" || !props.meal.meal_type
				? autoMealType()
				: props.meal.meal_type
		if (cookType.value === "restaurant") {
			restaurantName.value = props.meal.restaurant_name ?? ""
			nextTick(() => {
				showRestaurantInput.value = true
			})
		}
	}
</script>

<style lang="less" scoped>
	.modern-select {
		border-radius: 8px;
		border: 2px solid #e0e0e0;
		padding: 10px;
		font-size: 16px;
		// background-color: #fff;
		transition: border-color 0.3s ease, box-shadow 0.3s ease;
		&:focus {
			border-color: #ff6600; /* Orange accent */
			box-shadow: 0 0 5px rgba(255, 102, 0, 0.3);
			outline: none;
		}
	}

	/* Transparent inputs for this view */
	.transparent-form-select {
		background-color: transparent !important;
		color: #ffffff !important;
		border-color: rgba(255, 255, 255, 0.25);
		color-scheme: dark;
		background-image: url("data:image/svg+xml;charset=UTF-8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='white' viewBox='0 0 16 16'><path d='M4.646 6.646a.5.5 0 0 1 .708 0L8 9.293l2.646-2.647a.5.5 0 0 1 .708.708l-3 3a.5.5 0 0 1-.708 0l-3-3a.5.5 0 0 1 0-.708z'/></svg>");
		background-repeat: no-repeat;
		background-position: right 0.75rem center;
		background-size: 1.5em 1.5em;
		&:focus {
			box-shadow: 0 0 0 0.2rem rgba(34, 197, 94, 0.25);
			border-color: #22c55e;
		}
	}
</style>
